import {
    CartOrderPaymentStatus,
    PaymentService,
    CART_PAYMENT_RETURN_QUERY_PAYMENT_STATUS,
    CART_PAYMENT_RETURN_QUERY_ORDER_HASH
} from '../../shared/constants'
import { PaymentServiceType } from '@sim-api-enums/payment'
import { joinURL } from 'ufo'
import type { ApiReactiveService } from '@composable-api/service/api.reactive-service'
import type { ApiService } from '@composable-api/service/api.service'

export function useCart() {
    const store = useCartStore()
    return {
        ...store,
        ...storeToRefs(store),
    }
}

interface UseCartPaymentSummaryReturn {
    order: ComputedRef<InstanceType<typeof OrderModel> | null>
    paymentStatus: ComputedRef<CartOrderPaymentStatus>
    transformApiService<T extends ApiReactiveService<any> | ApiService<any>>(service: T): { getService(): T }
}

/**
 * A composable to handle the cart payment summary.
 */
export function useCartPaymentSummary(): UseCartPaymentSummaryReturn {
    const route = useRoute()
    const cartStore = useCartStore()

    const order = computed(() =>
        // If the latest order is a dummy one (unknown status), do not return an order
        cartStore.latestOrder?.id === -1
            ? null
            : cartStore.latestOrder ?? null
    )

    const paymentStatus = computed<CartOrderPaymentStatus>(() => {
        const status = route.query[CART_PAYMENT_RETURN_QUERY_PAYMENT_STATUS]
        if (status && !Array.isArray(status) && Object.values(CartOrderPaymentStatus).includes(status as any)) {
            return status as CartOrderPaymentStatus
        }

        if (cartStore.latestOrder?.id === -1) return CartOrderPaymentStatus.UNKNOWN
        if (order.value?.isPaid() === false && !order.value?.isToBePaidLater()) return CartOrderPaymentStatus.ERROR

        return CartOrderPaymentStatus.SUCCESS
    })

    const orderHash = computed<string | undefined>(() => {
        if (cartStore.latestOrder) return cartStore.latestOrder?.hash ?? undefined

        const hash = route.query[CART_PAYMENT_RETURN_QUERY_ORDER_HASH]
        if (hash && !Array.isArray(hash)) {
            return hash
        }
        return undefined
    })

    /**
     * Transforms an API service to automatically include the order hash if available.
     *
     * This is necessary to access guest orders without being logged in.
     */
    const transformApiService: UseCartPaymentSummaryReturn['transformApiService'] = (service) => {
        return {
            getService() {
                const transformedService = service
                if (orderHash.value) {
                    transformedService.setParam('hash', orderHash.value)
                }

                return transformedService
            },
        }
    }

    // throw an error if the order is missing and it isn't a dummy order
    if (order.value === null && cartStore.latestOrder?.id !== -1) {
        console.error(`[useCartPaymentSummary]: Order is \`null\` for route \`${route.fullPath}\`. This should never happen.`)
        throw createError({
            statusCode: 404,
            fatal: true,
        })
    }

    return {
        order: order as UseCartPaymentSummaryReturn['order'],
        paymentStatus: paymentStatus,
        transformApiService,
    }
}


export function useCartPayment() {
    const paymentReturnUrlBase = useFullUrl('/api/payment-return')
    const goPayClient = useClientGoPay()

    function createPaymentWrapper(handler: (orderId: number) => Promise<void>): (orderId: number | null | undefined) => Promise<void> {
        return async (orderId: number | null | undefined) => {
            if (!orderId) throw new Error('Order ID is missing.')

            await handler(orderId)

            await new Promise(() => {})
        }
    }

    const createGoPayPayment = createPaymentWrapper(async (orderId) => {
        const service = await import('@simploshop-services/GoPay.service').then(m => m.getGoPayApiService())
        const goPayResponsePromise = service.post({
            order_id: orderId,
            payload: null,
            return_url: joinURL(paymentReturnUrlBase, PaymentService.GoPay),
        })

        await goPayClient.checkout(
            goPayResponsePromise.then(r => r.getItem()?.gateUrl ?? null)
        )
    })

    const createCsobPayment = createPaymentWrapper(async (orderId) => {
        const service = await import('@simploshop-services/CsobPayment.service').then(m => m.getCsobPaymentApiService())
        const csobResponse = await service.post({
            order_id: orderId,
            return_uri: joinURL(paymentReturnUrlBase, PaymentService.CSOB),
            return_method: 'POST',
        })

        const gateUrl = csobResponse?.getItem()?.gatewayUri ?? null
        if (!gateUrl) {
            // TODO: throw a custom error with translated message
            throw new Error('CSOB payment gateway URL is missing.')
        }

        await navigateTo(gateUrl, {
            external: true,
        })
    })

    const createPayPalPayment = createPaymentWrapper(async (orderId) => {
        const service = await import('@simploshop-services/PayPalRequests.service').then(m => m.getPayPalRequestsApiService())
        const paypalResponse = await service.post({
            order_id: orderId,
            return_url: joinURL(paymentReturnUrlBase, PaymentService.PayPal),
        })

        // @ts-expect-error - TODO: add support for JSON responses without models
        const gateUrl = paypalResponse?.getItem()?.gate_url ?? null
        if (!gateUrl) {
            throw new Error('PayPal payment gateway URL is missing.')
        }

        await navigateTo(gateUrl, {
            external: true,
        })
    })

    const createTrustPayPayment = createPaymentWrapper(async (orderId) => {
        const service = await import('@simploshop-services/TrustPayRequests.service').then(m => m.getTrustPayRequestsApiService())
        const returnUrl = joinURL(paymentReturnUrlBase, PaymentService.TrustPay)
        const trustPayResponse = await service.post({
            order_id: orderId,
            success_url: returnUrl,
            error_url: returnUrl,
            cancel_url: returnUrl,
        })
        // @ts-expect-error - TODO: add support for JSON responses without models
        const gateUrl = trustPayResponse?.getItem()?.gate_url ?? null
        if (!gateUrl) {
            throw new Error('TrustPay payment gateway URL is missing.')
        }

        await navigateTo(gateUrl, {
            external: true,
        })
    })

    function createPayment(paymentServiceType: PaymentServiceType | null| undefined, orderId: number | null | undefined) {
        if (paymentServiceType === PaymentServiceType.GOPAY) {
            return createGoPayPayment(orderId)
        } else if (paymentServiceType === PaymentServiceType.CSOB) {
            return createCsobPayment(orderId)
        } else if (paymentServiceType === PaymentServiceType.PAYPAL) {
            return createPayPalPayment(orderId)
        } else if (paymentServiceType === PaymentServiceType.TRUSTPAY) {
            return createTrustPayPayment(orderId)
        }
    }

    return {
        createPayment,
    }
}
